import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ComputerIcon from "@mui/icons-material/Computer";
import CropOriginalIcon from "@mui/icons-material/CropOriginal";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Slider,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomSnackbar from "../components/CustomSnackbar";
import Item from "../components/Item";
import AdminStoryService from "../story/AdminStoryService";
import { getStoryDisplayTitle } from "../story/storyDisplayUtils";
import AdResourceService from "./AdResourceService";
import {
    ADMIN_ADVERTISEMENT_IPHONE_BACKGROUND_PATH,
    ADMIN_ADVERTISEMENT_PATH,
    advertisementResourceDetailPath,
    iphoneBackgroundCropPath,
    pcBackgroundCropPath,
} from "./advertisementRoutes";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const ORDER_BY_OPTIONS = [
    { value: "publishedAt:desc", label: "Published at (newest)" },
    { value: "publishedAt:asc", label: "Published at (oldest)" },
    { value: "createdAt:desc", label: "Created at (newest)" },
    { value: "createdAt:asc", label: "Created at (oldest)" },
];
const VALID_SORT_VALUES = new Set(ORDER_BY_OPTIONS.map((o) => o.value));
const DEFAULT_SORT = "createdAt:desc";
const PAGE_SIZE_STORAGE_KEY = "admin.ad.iphoneBackground.pageSize";
const SORT_STORAGE_KEY = "admin.ad.iphoneBackground.sort";

function readStoredPageSize() {
    try {
        const n = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY), 10);
        if (PAGE_SIZE_OPTIONS.includes(n)) return n;
    } catch {
        /* ignore */
    }
    return DEFAULT_PAGE_SIZE;
}

function readStoredSort() {
    try {
        const stored = localStorage.getItem(SORT_STORAGE_KEY);
        if (stored && VALID_SORT_VALUES.has(stored)) return stored;
    } catch {
        /* ignore */
    }
    return DEFAULT_SORT;
}
const IPHONE_VIEW = { targetW: 1290, targetH: 2796, displayH: 560 };
const PC_VIEW = { targetW: 1920, targetH: 1080, displayH: 420 };
const MAX_ZOOM_MULT = 4;

function cropViewport(view) {
    const displayH = view.displayH;
    const displayW = Math.round(displayH * (view.targetW / view.targetH));
    return { targetW: view.targetW, targetH: view.targetH, displayW, displayH };
}

function getStoryFullImageUrl(story) {
    if (!story || typeof story !== "object") return null;
    const full = story.imageUrl ?? story.image_url;
    if (typeof full === "string" && full.trim()) return full.trim();
    const preview = story.imagePreviewUrl ?? story.image_preview_url;
    if (typeof preview === "string" && preview.trim()) return preview.trim();
    const icon400 = story.imageIcon400Url ?? story.image_icon_400_url;
    if (typeof icon400 === "string" && icon400.trim()) return icon400.trim();
    return null;
}

function getStoryThumbUrl(story) {
    if (!story || typeof story !== "object") return null;
    const icon400 = story.imageIcon400Url ?? story.image_icon_400_url;
    if (typeof icon400 === "string" && icon400.trim()) return icon400.trim();
    const icon200 = story.imageIcon200Url ?? story.image_icon_200_url;
    if (typeof icon200 === "string" && icon200.trim()) return icon200.trim();
    return getStoryFullImageUrl(story);
}

function clamp(n, min, max) {
    return Math.min(max, Math.max(min, n));
}

function clampPan(panX, panY, zoom, imgW, imgH, cropW, cropH) {
    const drawnW = imgW * zoom;
    const drawnH = imgH * zoom;
    const minX = Math.min(0, cropW - drawnW);
    const minY = Math.min(0, cropH - drawnH);
    return {
        panX: clamp(panX, minX, 0),
        panY: clamp(panY, minY, 0),
    };
}

function minZoomForImage(imgW, imgH, displayW, displayH) {
    if (!(imgW > 0) || !(imgH > 0)) return 1;
    return Math.max(displayW / imgW, displayH / imgH);
}

function coverCropState(imgW, imgH, displayW, displayH) {
    const zoom = minZoomForImage(imgW, imgH, displayW, displayH);
    const centered = clampPan(
        (displayW - imgW * zoom) / 2,
        (displayH - imgH * zoom) / 2,
        zoom,
        imgW,
        imgH,
        displayW,
        displayH,
    );
    return { zoom, ...centered };
}

function cropFractions({ panX, panY, zoom, imgW, imgH }, displayW, displayH) {
    return {
        cropX: -panX / zoom / imgW,
        cropY: -panY / zoom / imgH,
        cropWidth: displayW / zoom / imgW,
        cropHeight: displayH / zoom / imgH,
    };
}

function CoverCropper({ imageUrl, crop, onCropChange, displayW, displayH }) {
    const frameRef = useRef(null);
    const dragRef = useRef(null);
    const cropRef = useRef(crop);
    cropRef.current = crop;
    const onCropChangeRef = useRef(onCropChange);
    onCropChangeRef.current = onCropChange;

    const imgW = crop.imgW;
    const imgH = crop.imgH;
    const minZ = minZoomForImage(imgW, imgH, displayW, displayH);
    const maxZ = minZ * MAX_ZOOM_MULT;

    const setZoomKeepingCenter = useCallback((nextZoom) => {
        const current = cropRef.current;
        const floor = minZoomForImage(current.imgW, current.imgH, displayW, displayH);
        const z = clamp(nextZoom, floor, floor * MAX_ZOOM_MULT);
        const cx = displayW / 2;
        const cy = displayH / 2;
        const imgX = (cx - current.panX) / current.zoom;
        const imgY = (cy - current.panY) / current.zoom;
        const panned = clampPan(
            cx - imgX * z,
            cy - imgY * z,
            z,
            current.imgW,
            current.imgH,
            displayW,
            displayH,
        );
        onCropChangeRef.current({ ...current, zoom: z, ...panned });
    }, [displayW, displayH]);

    useEffect(() => {
        const node = frameRef.current;
        if (!node) return undefined;
        const onWheel = (event) => {
            event.preventDefault();
            const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
            setZoomKeepingCenter(cropRef.current.zoom * factor);
        };
        node.addEventListener("wheel", onWheel, { passive: false });
        return () => node.removeEventListener("wheel", onWheel);
    }, [setZoomKeepingCenter]);

    const handlePointerDown = (event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        const current = cropRef.current;
        dragRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            panX: current.panX,
            panY: current.panY,
        };
    };

    const handlePointerMove = (event) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const current = cropRef.current;
        const panned = clampPan(
            drag.panX + (event.clientX - drag.x),
            drag.panY + (event.clientY - drag.y),
            current.zoom,
            current.imgW,
            current.imgH,
            displayW,
            displayH,
        );
        onCropChangeRef.current({ ...current, ...panned });
    };

    const handlePointerUp = (event) => {
        if (dragRef.current?.pointerId === event.pointerId) {
            dragRef.current = null;
        }
    };

    return (
        <Stack spacing={1.5} alignItems="center">
            <Box
                ref={frameRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                sx={{
                    position: "relative",
                    width: displayW,
                    height: displayH,
                    overflow: "hidden",
                    borderRadius: "28px",
                    border: "2px solid",
                    borderColor: "divider",
                    bgcolor: "#111",
                    cursor: "grab",
                    touchAction: "none",
                    userSelect: "none",
                    boxShadow: 3,
                    "&:active": { cursor: "grabbing" },
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    draggable={false}
                    sx={{
                        position: "absolute",
                        left: crop.panX,
                        top: crop.panY,
                        width: imgW * crop.zoom,
                        height: imgH * crop.zoom,
                        maxWidth: "none",
                        pointerEvents: "none",
                    }}
                />
            </Box>
            <Box sx={{ width: Math.max(displayW, 220) }}>
                <Typography variant="caption" color="text.secondary">
                    Zoom
                </Typography>
                <Slider
                    size="small"
                    min={minZ}
                    max={maxZ}
                    step={(maxZ - minZ) / 100}
                    value={crop.zoom}
                    onChange={(_, value) => setZoomKeepingCenter(Number(value))}
                />
            </Box>
            <Button
                size="small"
                onClick={() => onCropChange({ ...crop, ...coverCropState(imgW, imgH, displayW, displayH) })}
                sx={{ textTransform: "none" }}
            >
                Reset crop
            </Button>
        </Stack>
    );
}

export function PcBackgroundFromStoryMain() {
    return <IphoneBackgroundFromStoryMain mode="pc" />;
}

export default function IphoneBackgroundFromStoryMain({ mode = "iphone" }) {
    const isPc = mode === "pc";
    const viewport = cropViewport(isPc ? PC_VIEW : IPHONE_VIEW);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const storyIdParam = (searchParams.get("storyId") || "").trim();
    const showCrop = isPc || Boolean(storyIdParam);
    const [keywordInput, setKeywordInput] = useState("");
    const [keyword, setKeyword] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(readStoredPageSize);
    const [sort, setSort] = useState(readStoredSort);
    const [stories, setStories] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [storyLoading, setStoryLoading] = useState(false);
    const [fullImageUrl, setFullImageUrl] = useState(null);
    const [crop, setCrop] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, severity: "info", message: "" });

    const listRequestRef = useRef(0);

    const loadStories = useCallback(async () => {
        const requestId = ++listRequestRef.current;
        setLoading(true);
        const result = await AdminStoryService.list(
            page - 1,
            pageSize,
            sort,
            "",
            keyword,
            true,
        );
        if (requestId !== listRequestRef.current) return;
        setLoading(false);
        if (!result.success) {
            setStories([]);
            setTotalPages(0);
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || "Failed to load standalone images",
            });
            return;
        }
        setStories(result.data?.content ?? []);
        const totalElements = Number(result.data?.totalElements) || 0;
        const nextPageCount = Math.max(1, Math.ceil(totalElements / pageSize) || 1);
        setTotalPages(totalElements > 0 ? nextPageCount : 0);
        if (totalElements > 0 && page > nextPageCount) {
            setPage(nextPageCount);
        }
    }, [page, pageSize, sort, keyword]);

    useEffect(() => {
        if (showCrop) return undefined;
        loadStories();
        return undefined;
    }, [loadStories, showCrop]);

    useEffect(() => {
        if (!storyIdParam) {
            setSelected(null);
            setStoryLoading(false);
            return undefined;
        }
        let cancelled = false;
        setSelected(null);
        setStoryLoading(true);
        (async () => {
            const result = await AdminStoryService.getById(storyIdParam);
            if (cancelled) return;
            setStoryLoading(false);
            if (!result.success || !result.data) {
                setSelected(null);
                setSnackbar({
                    open: true,
                    severity: "error",
                    message: result.error || "Story not found",
                });
                return;
            }
            setSelected(result.data);
        })();
        return () => {
            cancelled = true;
        };
    }, [storyIdParam]);

    useEffect(() => {
        if (!selected) {
            setFullImageUrl(null);
            setCrop(null);
            setImageLoading(false);
            return undefined;
        }
        const url = getStoryFullImageUrl(selected);
        if (!url) {
            setFullImageUrl(null);
            setCrop(null);
            setSnackbar({
                open: true,
                severity: "error",
                message: "This story has no high-quality image URL",
            });
            return undefined;
        }
        setFullImageUrl(url);
        setImageLoading(true);
        setCrop(null);
        const img = new Image();
        img.onload = () => {
            const imgW = img.naturalWidth;
            const imgH = img.naturalHeight;
            setCrop({
                imgW,
                imgH,
                ...coverCropState(imgW, imgH, viewport.displayW, viewport.displayH),
            });
            setImageLoading(false);
        };
        img.onerror = () => {
            setImageLoading(false);
            setCrop(null);
            setSnackbar({
                open: true,
                severity: "error",
                message: "Failed to load the standalone image",
            });
        };
        img.src = url;
        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [selected, viewport.displayW, viewport.displayH]);

    useEffect(() => {
        const next = keywordInput.trim();
        if (next === keyword) return undefined;
        const handle = setTimeout(() => {
            setKeyword(next);
            setPage(1);
        }, 250);
        return () => clearTimeout(handle);
    }, [keywordInput, keyword]);

    const handleSortChange = (event) => {
        const next = event.target.value;
        if (!VALID_SORT_VALUES.has(next)) return;
        setSort(next);
        setPage(1);
        try {
            localStorage.setItem(SORT_STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    };

    const handlePageSizeChange = (event) => {
        const next = Number(event.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        setPageSize(next);
        setPage(1);
        try {
            localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next));
        } catch {
            /* ignore */
        }
    };

    const handleSave = async () => {
        const storyId = selected?.id;
        if (!storyId || !crop || saving) return;
        setSaving(true);
        const saveCrop = isPc
            ? AdResourceService.createPcBackgroundFromStoryCrop
            : AdResourceService.createFromStoryCrop;
        const result = await saveCrop({
            storyId,
            ...cropFractions(crop, viewport.displayW, viewport.displayH),
        });
        setSaving(false);
        if (!result.success) {
            setSnackbar({
                open: true,
                severity: "error",
                message: result.error || (isPc ? "Failed to save PC background" : "Failed to save iPhone background"),
            });
            return;
        }
        const id = result.data?.id;
        setSnackbar({
            open: true,
            severity: "success",
            message: isPc
                ? "Saved 1920×1080 JPEG PC background"
                : "Saved 1290×2796 JPEG iPhone background",
        });
        if (id) {
            navigate(advertisementResourceDetailPath(id));
        }
    };

    const selectedTitle = useMemo(
        () => (selected ? getStoryDisplayTitle(selected) : ""),
        [selected],
    );

    return (
        <Box sx={{ p: 3 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                    if (showCrop) {
                        navigate(ADMIN_ADVERTISEMENT_IPHONE_BACKGROUND_PATH);
                        return;
                    }
                    navigate(ADMIN_ADVERTISEMENT_PATH);
                }}
                sx={{ mb: 2, textTransform: "none" }}
            >
                {showCrop ? "Back to App Background" : "Back to resources"}
            </Button>

            <Item sx={{ textAlign: "left" }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                    {showCrop
                        ? isPc
                            ? "PC background from standalone image"
                            : "iPhone background from standalone image"
                        : "App Background"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                    {showCrop ? (
                        isPc ? (
                            <>
                                Drag and zoom to cut a <strong>1920×1080</strong> area for the PC app background.
                            </>
                        ) : (
                            <>
                                Drag and zoom to cut a <strong>1290×2796</strong> portrait area for the iPhone app background.
                            </>
                        )
                    ) : (
                        <>
                            Pick a standalone image. The iPhone icon opens the 1290×2796 crop. The PC icon opens the 1920×1080 crop.
                        </>
                    )}
                </Typography>

                {showCrop && !storyIdParam ? (
                    <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                        Choose a standalone image from App Background, then open the PC crop.
                    </Typography>
                ) : showCrop && (storyLoading || !selected) ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        {storyLoading ? (
                            <CircularProgress />
                        ) : (
                            <Typography color="text.secondary">This story could not be opened.</Typography>
                        )}
                    </Box>
                ) : showCrop ? (
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={3}
                        alignItems={{ xs: "center", md: "flex-start" }}
                    >
                        {imageLoading || !crop || !fullImageUrl ? (
                            <Box
                                sx={{
                                    width: viewport.displayW,
                                    height: viewport.displayH,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        ) : (
                            <CoverCropper
                                imageUrl={fullImageUrl}
                                crop={crop}
                                onCropChange={setCrop}
                                displayW={viewport.displayW}
                                displayH={viewport.displayH}
                            />
                        )}
                        <Stack spacing={1.5} sx={{ minWidth: 240, maxWidth: 360 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {selectedTitle}
                            </Typography>
                            {crop ? (
                                <Typography variant="body2" color="text.secondary">
                                    Source {crop.imgW}×{crop.imgH}. Output {viewport.targetW}×{viewport.targetH} JPEG.
                                </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary">
                                Drag the preview to choose the slice. Zoom in to crop a tighter area.
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={
                                    saving ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : (
                                        <CropOriginalIcon />
                                    )
                                }
                                disabled={saving || !crop}
                                onClick={handleSave}
                                sx={{ textTransform: "none", alignSelf: "flex-start" }}
                            >
                                {isPc ? "Save as PC background" : "Save as iPhone background"}
                            </Button>
                        </Stack>
                    </Stack>
                ) : (
                    <>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            flexWrap="wrap"
                            alignItems={{ xs: "stretch", sm: "center" }}
                            sx={{ mb: 2 }}
                        >
                            <TextField
                                size="small"
                                label="Search"
                                placeholder="Search by title"
                                value={keywordInput}
                                onChange={(event) => setKeywordInput(event.target.value)}
                                sx={{ minWidth: 220, maxWidth: 360, flex: 1 }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel id="iphone-bg-order-by-label">Order by</InputLabel>
                                <Select
                                    labelId="iphone-bg-order-by-label"
                                    label="Order by"
                                    value={sort}
                                    onChange={handleSortChange}
                                >
                                    {ORDER_BY_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>

                        {loading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <CircularProgress />
                            </Box>
                        ) : stories.length === 0 ? (
                            <Typography color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
                                No standalone images found.
                            </Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {stories.map((story) => {
                                    const thumb = getStoryThumbUrl(story);
                                    const title = getStoryDisplayTitle(story);
                                    const hasImage = Boolean(getStoryFullImageUrl(story));
                                    return (
                                        <Grid key={story.id} size={{ xs: 12, sm: 6, md: 4 }}>
                                            <Card variant="outlined" sx={{ height: "100%" }}>
                                                <Box
                                                    sx={{
                                                        aspectRatio: "1 / 1",
                                                        bgcolor: "action.hover",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {thumb ? (
                                                        <Box
                                                            component="img"
                                                            src={thumb}
                                                            alt=""
                                                            sx={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "cover",
                                                                display: "block",
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                height: "100%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}
                                                        >
                                                            <Typography variant="caption" color="text.secondary">
                                                                No image
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                                <CardContent sx={{ py: 1.25 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            display: "-webkit-box",
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: "vertical",
                                                            overflow: "hidden",
                                                        }}
                                                    >
                                                        {title}
                                                    </Typography>
                                                </CardContent>
                                                <Stack direction="row" spacing={0.5} sx={{ px: 1, pb: 1 }}>
                                                    <Tooltip title="iPhone background">
                                                        <span>
                                                            <IconButton
                                                                aria-label="iPhone background"
                                                                disabled={!hasImage}
                                                                onClick={() => navigate(iphoneBackgroundCropPath(story.id))}
                                                                sx={{ minWidth: 44, minHeight: 44 }}
                                                            >
                                                                <PhoneIphoneIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="PC background">
                                                        <span>
                                                            <IconButton
                                                                aria-label="PC background"
                                                                disabled={!hasImage}
                                                                onClick={() => navigate(pcBackgroundCropPath(story.id))}
                                                                sx={{ minWidth: 44, minHeight: 44 }}
                                                            >
                                                                <ComputerIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems="center"
                            justifyContent="center"
                            spacing={2}
                            sx={{ mt: 3 }}
                        >
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel id="iphone-bg-page-size-label">Page size</InputLabel>
                                <Select
                                    labelId="iphone-bg-page-size-label"
                                    label="Page size"
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                >
                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                        <MenuItem key={n} value={n}>
                                            {n}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {totalPages > 1 ? (
                                <Pagination
                                    color="primary"
                                    count={totalPages}
                                    page={Math.min(page, totalPages)}
                                    onChange={(_, value) => setPage(value)}
                                    disabled={loading}
                                    showFirstButton
                                    showLastButton
                                />
                            ) : null}
                        </Stack>
                    </>
                )}
            </Item>

            <CustomSnackbar
                open={snackbar.open}
                setOpen={(v) => setSnackbar((s) => ({ ...s, open: v }))}
                message={snackbar.message}
                severity={snackbar.severity}
            />
        </Box>
    );
}
